/**
 * @file components/chat/ChatApp.jsx
 * @description Primary component that orchestrates the entire chat interface
 * REFACTORED: Now uses split contexts (useChatSession, useFileUpload)
 * 
 * @component ChatApp
 */

// --- Core React and Hook Imports ---
import React, { useEffect, useRef, useState } from 'react';

// --- Import specific context hooks instead of useChat ---
import { useAuth } from '../../context/AuthContext'; 
import { useChatSession } from '../../context/ChatSessionContext';
import { useFileUpload } from '../../context/FileUploadContext';

// --- Child Component Imports (now from organized folders) ---
import WelcomeModelSelector from './WelcomeModelSelector';
import CompactModelSelector from './CompactModelSelector';
import MemoizedChatMessage from './ChatMessage';
import StreamingMessage from './StreamingMessage';
import LoadingIndicator from '../common/LoadingIndicator';
import SearchView from '../search/SearchView';
import FileUploadButton from '../file-upload/FileUploadButton';
import FileChip from '../file-upload/FileChip';
import WebSearchToggle from '../file-upload/WebSearchToggle';
import SettingsPage from '../settings/SettingsPage';
import ScrollbarBookmarks from './ScrollbarBookmarks';

// --- Style and Icon Imports ---
import styles from '../../styles/ChatApp.module.css';
import { SendHorizontal, Square, ChevronLeft, ChevronRight, Menu, AlertCircle } from 'lucide-react';

/**
 * ChatApp is the main component that orchestrates the entire chat interface.
 * Uses specific contexts for better performance and separation of concerns
 */
export default function ChatApp({ onMobileBurgerClick }) {
    // --- State from specific contexts 
    
    // From AuthContext
    const { userProfile } = useAuth();
    const useOwnKey = userProfile?.useOwnKey ?? false;

    // From ChatSessionContext
    const {
        messages,
        inputValue,
        setInputValue,
        isWaitingForResponse,
        isRouting,
        selectedModel,
        isSubmitting,
        viewMode,
        stopGenerating,
        streamingContent,
        streamingMessageMeta,
        startNewChat,
    } = useChatSession();
    
    // From FileUploadContext
    const {
        pendingFiles,
        uploadingFiles,
        webSearchEnabled,
        setWebSearchEnabled,
        addFiles,
        removeFile,
        handleSubmit,
        uploadError,
        setUploadError,
        resetFileUpload,
    } = useFileUpload();

    // Combine new-chat with file state reset so pending attachments are cleared too
    const handleNewChat = () => {
        startNewChat();
        resetFileUpload();
    };

    // --- Refs for DOM Manipulation ---
    const chatMainRef = useRef(null); // Reference to the main container
    const chatEndRef = useRef(null); // For auto-scrolling to the latest message
    const textareaRef = useRef(null); // For auto-resizing the input textarea
    const filesScrollRef = useRef(null); // For horizontal file chips scroll

    // --- File scroll arrow visibility ---
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollArrows = () => {
        const el = filesScrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 1);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    };

    // Re-check after files change (DOM re-renders before the effect fires)
    useEffect(() => {
        const timer = setTimeout(updateScrollArrows, 0);
        return () => clearTimeout(timer);
    }, [pendingFiles]);

    console.log(`ChatApp: Rendering chat interface, ${messages.length} messages, view mode: ${viewMode}`);

    // --- UI Effects ---

    // Effect for view mode changes - handles initial scroll position
    useEffect(() => {
        if (viewMode === 'search' || (viewMode === 'chat' && showWelcomeScreen)) {
            // Scroll to top when entering search view
            setTimeout(() => {
                if (chatMainRef.current) {
                    chatMainRef.current.scrollTop = 0;
                }
            }, 50);
        }
        else if (viewMode === 'chat' && messages.length > 0 && !showWelcomeScreen) {
            // Scroll to bottom when entering chat view
            setTimeout(() => {
                chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
        }
    }, [viewMode, messages.length, isWaitingForResponse]); // isWaitingForResponse drives showWelcomeScreen

/*
    CURRENTLY IT IS BREAKING THE SCROLLING, HENCE COMMENTING IT
    // Effect #2: for chat updates - keeps chat scrolled to bottom during conversation
    useEffect(() => {
        if (viewMode === 'chat') {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isWaitingForResponse, streamingContent, pendingFiles]);
*/
    // Effect #3: Auto-resize the prompt input textarea based on content
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            // Temporarily set height to 'auto' to calculate natural scrollHeight
            textarea.style.height = 'auto';
            // Set the height to the calculated scrollHeight to fit content
            if (inputValue) {
                textarea.style.height = `${textarea.scrollHeight}px`;
            }
        }
    }, [inputValue]);

    // --- Derived State for Render Logic ---
    const showWelcomeScreen = messages.length === 0 && !isWaitingForResponse && viewMode === 'chat';

    // Check if there's any activity preventing submission
    const isUploading = uploadingFiles.length > 0;
    const hasContent = inputValue.trim() || pendingFiles.length > 0;

    // Logic block for dynamic credit/key checking
    const getProviderForModel = (modelId) => {
        const id = modelId.toLowerCase();
        if (id.includes('gpt')) return 'openai';
        if (id.includes('claude')) return 'anthropic';
        if (id.includes('gemini')) return 'google';
        return 'openrouter'; // Default or for OpenRouter native models
    };
    const modelProvider = getProviderForModel(selectedModel);
    const hasOpenRouterKey = !!userProfile?.apiKeys?.openrouter?.isSet; // Direct check
    const hasProviderKey = !!userProfile?.apiKeys?.[modelProvider]?.isSet; // Dynamic check for openai, google, etc.
    const hasSufficientCredits = parseFloat(userProfile?.usageCredits || 0) > -5;
    // User can submit if they have credits OR the super-key (OpenRouter) OR a specific key for the model.
    const canUseSelectedModel = useOwnKey 
        ? (hasOpenRouterKey || hasProviderKey || hasSufficientCredits)  // BYOK mode: only check keys
        : hasSufficientCredits;                 // Credits mode: only check credits
    // This is our new master flag. The user is blocked if they cannot use the selected model.
    const isEffectivelyBlocked = !canUseSelectedModel;

    return (
        <div className={styles.chatWrapper}>
            {/*Conditionally render SettingsPage */}
            {viewMode === 'settings' ? (
                <SettingsPage />
            ) : (
                <>
                    <header className={styles.chatHeader}>
                        <button
                            className={styles.mobileMenuBtn}
                            onClick={onMobileBurgerClick}
                            aria-label="Open navigation"
                        >
                            <Menu size={22} />
                        </button>
                        {/* On mobile the title acts as a "new chat" shortcut (tap logo = new chat).
                            We render a <button> only on mobile via CSS; on desktop it stays a div. */}
                        <button
                            className={`${styles.headerCenter} ${styles.headerCenterClickable}`}
                            onClick={handleNewChat}
                            title="New chat"
                            type="button"
                        >
                            <h1 className={styles.headerTitle}>Arianna v0.1</h1>
                            <p className={styles.headerSubtitle}>The Intelligent Routing Engine</p>
                        </button>
                    </header>

                    {/* Wrapper gives the custom scrollbar a stable viewport to anchor to.
                        chatMain scrolls inside it; ScrollbarBookmarks sits alongside as a sibling. */}
                    <div className={styles.chatScrollArea}>
                        <main className={styles.chatMain} ref={chatMainRef}>
                            {/* --- Conditional Rendering Logic --- */}
                            {viewMode === 'search' ? (
                                <SearchView />
                            ) : showWelcomeScreen ? (
                                <WelcomeModelSelector />
                            ) : (
                                <>
                                    {/* Display chat history */}
                                    {messages.map((msg, index) => {
                                        // If an assistant message has routing details...
                                        if (msg.role === 'assistant' && msg.routingDetails) {
                                            // Check if the previous message is ALREADY our system message.
                                            // This prevents duplication during a live session.
                                            const prevMessage = messages[index - 1];
                                            const isSystemMessageAlreadyPresent =
                                                prevMessage &&
                                                prevMessage.role === 'system' &&
                                                prevMessage.content.startsWith('Routing complete!');

                                            if (isSystemMessageAlreadyPresent) {
                                                // Just render the assistant message, the system message is already visible.
                                                return <MemoizedChatMessage key={index} message={msg} />;
                                            } else {
                                                // This handles the case for reloaded conversations.
                                                // Generate and prepend the system message.
                                                const benchmark = msg.routingDetails.benchmark || 'general';
                                                const model = msg.routingDetails.model || 'the selected model';
                                                const formattedBenchmark = benchmark.charAt(0).toUpperCase() + benchmark.slice(1).replace(/_/g, ' ');

                                                const systemMessageContent =
                                                    `Routing complete!\n` +
                                                    `Best model is: ${model}\n` +
                                                    `Optimized for: ${formattedBenchmark} benchmark`;

                                                const systemMessage = { role: 'system', content: systemMessageContent };

                                                return (
                                                    <React.Fragment key={`${msg.timestamp || index}-routed`}>
                                                        <MemoizedChatMessage message={systemMessage} />
                                                        <MemoizedChatMessage message={msg} />
                                                    </React.Fragment>
                                                );
                                            }
                                        }

                                        // Otherwise, render the message normally
                                        return <MemoizedChatMessage key={index} message={msg} />;
                                    })}

                                    {/* Render streaming message separately - only this re-renders! */}
                                    {streamingMessageMeta && streamingContent && (
                                        <StreamingMessage
                                            content={streamingContent}
                                            meta={streamingMessageMeta}
                                        />
                                    )}
                                </>
                            )}

                            {/* Loading indicator */}
                            {viewMode === 'chat' && !showWelcomeScreen && isWaitingForResponse && !streamingContent && (
                                <LoadingIndicator type={isRouting ? 'routing' : 'generating'} />
                            )}

                            {/* Auto-scroll target */}
                            <div ref={chatEndRef} />
                        </main>

                        {/* Custom scrollbar — sibling of chatMain so it anchors to the
                            visible viewport height, not the scroll content height. */}
                        <ScrollbarBookmarks
                            scrollContainerRef={chatMainRef}
                            messages={messages}
                        />
                    </div>

                    {/* Footer with prompt input form */}
                    {viewMode === 'chat' && (
                        <footer className={styles.chatFooter}>
                            <form onSubmit={handleSubmit} className={styles.promptForm}>
                                <div className={styles.inputBox}>

                                    {/* Upload / validation error banner */}
                                    {uploadError && (
                                        <div className={styles.uploadErrorBanner}>
                                            <AlertCircle size={16} className={styles.uploadErrorIcon} />
                                            <span>{uploadError}</span>
                                            <button
                                                type="button"
                                                className={styles.uploadErrorDismiss}
                                                onClick={() => setUploadError(null)}
                                                aria-label="Dismiss error"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    )}

                                    {/* Row 1: Attached files (hidden when empty) */}
                                    {pendingFiles.length > 0 && (
                                        <div className={styles.inputRowFiles}>
                                            <button
                                                type="button"
                                                className={styles.filesScrollBtn}
                                                onClick={() => filesScrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
                                                aria-label="Scroll files left"
                                                tabIndex={-1}
                                                style={{ visibility: canScrollLeft ? 'visible' : 'hidden' }}
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            <div
                                                className={styles.filesScrollTrack}
                                                ref={filesScrollRef}
                                                onScroll={updateScrollArrows}
                                            >
                                                {pendingFiles.map((fileInfo) => (
                                                    <FileChip
                                                        key={fileInfo.id}
                                                        file={fileInfo.file}
                                                        fileId={fileInfo.id}
                                                        onRemove={removeFile}
                                                        status={fileInfo.status}
                                                        progress={fileInfo.progress}
                                                    />
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                className={styles.filesScrollBtn}
                                                onClick={() => filesScrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
                                                aria-label="Scroll files right"
                                                tabIndex={-1}
                                                style={{ visibility: canScrollRight ? 'visible' : 'hidden' }}
                                            >
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    )}

                                    {/* Row 2: Text input */}
                                    <div className={styles.inputRowText}>
                                        <textarea
                                            ref={textareaRef}
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey && !isUploading) {
                                                    e.preventDefault();
                                                    handleSubmit(e);
                                                }
                                            }}
                                            placeholder={
                                                isEffectivelyBlocked
                                                    ? useOwnKey
                                                        ? `API key required for ${modelProvider} models`
                                                        : "Credits required to continue"
                                                    : isUploading
                                                    ? "Uploading files..."
                                                    : isSubmitting
                                                    ? "Sending..."
                                                    : `Message ${selectedModel}...`
                                            }
                                            className={styles.promptInput}
                                            rows="1"
                                            disabled={isWaitingForResponse || isEffectivelyBlocked || isUploading || isSubmitting}
                                        />
                                    </div>

                                    {/* Row 3: Tools bar */}
                                    <div className={styles.inputRowTools}>
                                        <div className={styles.toolsLeft}>
                                            <FileUploadButton
                                                onFilesSelected={addFiles}
                                                onValidationError={setUploadError}
                                                disabled={isWaitingForResponse || isEffectivelyBlocked || isUploading}
                                                currentFiles={pendingFiles}
                                            />
                                            <WebSearchToggle
                                                enabled={webSearchEnabled}
                                                onToggle={() => setWebSearchEnabled(!webSearchEnabled)}
                                                disabled={isWaitingForResponse || isEffectivelyBlocked}
                                            />
                                        </div>
                                        <div className={styles.toolsRight}>
                                            <CompactModelSelector />
                                            {/* Submit / Stop button */}
                                            {isWaitingForResponse && streamingContent ? (
                                                <button
                                                    type="button"
                                                    onClick={stopGenerating}
                                                    className={styles.stopButton}
                                                    title="Stop generating"
                                                >
                                                    <Square size={20} />
                                                </button>
                                            ) : (
                                                <button
                                                    type="submit"
                                                    disabled={isWaitingForResponse || !hasContent || isEffectivelyBlocked || isUploading || isSubmitting}
                                                    className={styles.submitButton}
                                                    title={
                                                        isEffectivelyBlocked
                                                            ? useOwnKey
                                                                ? "Add API key in settings"
                                                                : "Buy credits to continue"
                                                            : isUploading
                                                            ? "Uploading files..."
                                                            : isSubmitting
                                                            ? "Sending..."
                                                            : "Send prompt"
                                                    }
                                                >
                                                    <SendHorizontal size={20} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </form>

                            {/* Credits status */}
                            <div className={styles.promptCounter}>
                                {useOwnKey ? (
                                    <span>Using your own API keys for available models</span>
                                ) : (
                                    <span>{userProfile === null ? '...' : `${Math.round(parseFloat(userProfile.usageCredits || 0))} credits remaining`}</span>
                                )}
                            </div>
                        </footer>
                    )}
                </>
            )}
        </div>
    );
}